import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { ArrowLeft, Check, ImageIcon, Info, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { MultiSelect } from '../../components/ui/multi-select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../components/ui/utils';
import AccessRestricted from '../../components/AccessRestricted';
import { LoadingPageSkeleton } from '../../components/common/LoadingPageSkeleton';
import { LandingPagePreview } from '../../components/learning/LandingPagePreview';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import {
    Course,
    CourseContentType,
    CourseFormOptions,
    CoursePayload,
    CourseVisibilityType,
    ScormStatus,
} from '../../types/learning.types';
import learningApi from '../../utils/learning-api';

/**
 * New / Edit Course (LEARNING.md §5.2).
 *
 * One screen for both routes: `/learning/courses/new` starts blank, `/learning/courses/:id/edit`
 * loads the course through `courses.get` and seeds every field, including the visibility rules.
 * The right column is a live Browse Library card, so HR sees exactly what learners will.
 *
 * A SCORM course is the odd one out: its .zip is uploaded like any other file, but the server
 * then unpacks it in the background, so the form shows a processing state and polls until the
 * package is `ready` before it will allow Publish (LEARNING.md §13.1).
 *
 * Thumbnail and content files upload to S3 through `/api/learning/uploads/*` BEFORE the course is
 * saved, and the returned key/url travel on the course payload — the file has to exist before a
 * course row can point at it.
 */

/** Category colours are stored as 6-digit hex (CourseCategory.colour); anything else is treated as unset. */
const HEX_COLOUR = /^#[0-9a-f]{6}$/i;

/** Content types the form can build. SCORM uploads a package, not a single document. */
const CONTENT_TYPES: { value: CourseContentType; label: string; accept: string }[] = [
    { value: 'PDF', label: 'PDF document', accept: 'PDF document (.pdf)' },
    { value: 'PPTX', label: 'PowerPoint (PPTX)', accept: 'PowerPoint deck (.pptx)' },
    { value: 'SCORM', label: 'SCORM package', accept: 'SCORM package (.zip)' },
];

/**
 * What happened to a SCORM package: the STATE only, so it never competes with the next-step
 * callout in the header. A package is unpacked on the server after the course is saved, so for
 * a while the honest answer is "not yet".
 */
const ScormPackageNote: React.FC<{ course: Course | null; hasFile: boolean }> = ({ course, hasFile }) => {
    const status = course?.scormStatus ?? null;

    const tone = status === 'failed'
        ? 'border-destructive/30 bg-destructive/5 text-destructive'
        : status === 'ready'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
            : 'border-border bg-muted/40 text-muted-foreground';

    let body: React.ReactNode;
    if (status === 'ready') {
        body = (
            <>
                <span className="font-semibold">Package ready.</span> SCORM {course?.scormVersion}
                {' · '}{course?.scormFileCount} file(s){' · '}launches{' '}
                <span className="font-mono">{course?.scormLaunchPath}</span>
            </>
        );
    } else if (status === 'failed') {
        body = (
            <>
                <span className="font-semibold">This package could not be processed.</span>{' '}
                {course?.scormError || 'Unknown error.'}
            </>
        );
    } else if (status === 'pending' || status === 'processing') {
        body = <span className="font-semibold">Unpacking the package…</span>;
    } else if (hasFile) {
        body = <>Package attached — not unpacked yet.</>;
    } else {
        body = <>No package attached yet.</>;
    }

    return <div className={cn('rounded-lg border px-3.5 py-3 text-xs leading-relaxed', tone)}>{body}</div>;
};

/**
 * The next step HR has to take before a SCORM course can be published, or null when there is
 * nothing to say (not a SCORM course, or the package is ready).
 *
 * It is rendered beside the header buttons rather than with the content field because a
 * disabled control shows no tooltip: without a visible reason where the decision is made, a
 * SCORM course just looks permanently unpublishable.
 */
const scormNextStepFor = (
    contentType: CourseContentType,
    hasFile: boolean,
    status: ScormStatus | null,
): string | null => {
    if (contentType !== 'SCORM') return null;
    if (status === 'ready') return null;
    if (!hasFile) {
        return 'SCORM courses are unpacked on the server before they can be published. Attach the package (.zip), then Save as Draft to unpack it.';
    }
    if (status === 'pending' || status === 'processing') {
        return 'The package is being unpacked. Publish unlocks as soon as it finishes — you can leave this page.';
    }
    if (status === 'failed') {
        return 'The package could not be unpacked, so this course cannot be published yet. Saving again retries it.';
    }
    return 'Save as Draft to unpack the package — Publish unlocks as soon as it finishes on the server.';
};

const VISIBILITY_OPTIONS: { value: CourseVisibilityType; label: string; description: string }[] = [
    {
        value: 'Everyone',
        label: 'Everyone',
        description: 'Every learner can find it in the library and self-enrol.',
    },
    {
        value: 'Department',
        label: 'Specific departments',
        description: 'Only learners in the departments you pick below see it in the library.',
    },
    {
        value: 'JobRole',
        label: 'Specific job roles',
        description: 'Only learners with the job roles you pick below see it in the library.',
    },
];

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
        {children}
    </div>
);

export default function CourseFormPage() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const ability = useAbility(AbilityContext);

    const isEdit = Boolean(id);
    const parsedId = id ? Number(id) : NaN;
    const courseId = Number.isFinite(parsedId) ? parsedId : null;
    const canSave = isEdit
        ? ability.can(ACTIONS.UPDATE, RESOURCES.COURSE)
        : ability.can(ACTIONS.CREATE, RESOURCES.COURSE);

    const [options, setOptions] = useState<CourseFormOptions | null>(null);
    const [course, setCourse] = useState<Course | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState<'draft' | 'publish' | null>(null);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [contentType, setContentType] = useState<CourseContentType>('PDF');
    const [duration, setDuration] = useState('');
    const [passScore, setPassScore] = useState('');
    const [showInOnboarding, setShowInOnboarding] = useState(false);
    const [issuesCertificate, setIssuesCertificate] = useState(false);
    const [certificateValidity, setCertificateValidity] = useState('');
    const [visibilityType, setVisibilityType] = useState<CourseVisibilityType>('Everyone');
    const [departmentCodes, setDepartmentCodes] = useState<string[]>([]);
    const [jobRoles, setJobRoles] = useState<string[]>([]);
    // `previewUrl` is a freshly signed GET for immediate display; `url`/`key` are what get
    // persisted (the canonical URL + key, re-signed on every read).
    const [thumbnail, setThumbnail] = useState<{ url: string; key: string; previewUrl?: string } | null>(null);
    const [contentFile, setContentFile] = useState<{ url: string; key: string } | null>(null);
    const [uploading, setUploading] = useState<'thumbnail' | 'content' | null>(null);

    useEffect(() => {
        if (!canSave) return;
        let active = true;

        (async () => {
            try {
                setLoading(true);
                const [formOptions, existing] = await Promise.all([
                    learningApi.courses.options(),
                    courseId !== null ? learningApi.courses.get(courseId) : Promise.resolve(null),
                ]);
                if (!active) return;

                setOptions(formOptions);
                if (!existing) return;

                setCourse(existing);
                setTitle(existing.title ?? '');
                setDescription(existing.description ?? '');
                setCategoryId(existing.categoryId ? String(existing.categoryId) : '');
                // Every content type the API can return is now representable in the select.
                setContentType(existing.contentType);
                setPassScore(
                    existing.passScorePercent != null ? String(existing.passScorePercent) : '',
                );
                setDuration(
                    existing.estimatedDurationMinutes != null
                        ? String(existing.estimatedDurationMinutes)
                        : '',
                );
                setShowInOnboarding(existing.showInOnboarding);
                setIssuesCertificate(existing.issuesCertificate);
                setCertificateValidity(
                    existing.certificateValidityMonths != null
                        ? String(existing.certificateValidityMonths)
                        : '',
                );
                setThumbnail(
                    existing.thumbnailUrl && existing.thumbnailKey
                        ? { url: existing.thumbnailUrl, key: existing.thumbnailKey }
                        : null,
                );
                setContentFile(
                    existing.contentFileUrl && existing.contentFileKey
                        ? { url: existing.contentFileUrl, key: existing.contentFileKey }
                        : null,
                );

                const rules = existing.visibility ?? [];
                const codes = rules
                    .filter((rule) => rule.visibilityType === 'Department' && rule.departmentCode)
                    .map((rule) => rule.departmentCode as string);
                const roles = rules
                    .filter((rule) => rule.visibilityType === 'JobRole' && rule.jobRole)
                    .map((rule) => rule.jobRole as string);
                setDepartmentCodes(codes);
                setJobRoles(roles);
                setVisibilityType(
                    rules[0]?.visibilityType
                    ?? (codes.length ? 'Department' : roles.length ? 'JobRole' : 'Everyone'),
                );
            } catch (err) {
                if (active) toast.error(err instanceof Error ? err.message : 'Failed to load course');
            } finally {
                if (active) setLoading(false);
            }
        })();

        return () => {
            active = false;
        };
    }, [canSave, courseId]);

    /** The category list, keeping an archived category visible when it's the course's own. */
    const categoryOptions = useMemo(() => {
        const list = options?.categories ?? [];
        const current = course?.category ?? null;
        if (current && !list.some((category) => category.id === current.id)) {
            return [current, ...list];
        }
        return list;
    }, [options, course]);

    /** SCORM only: the score a package's report has to reach for the course to be passed. */
    const passScorePercent = useMemo(() => {
        if (contentType !== 'SCORM') return null;
        const percent = Number(passScore);
        return passScore.trim() !== '' && Number.isFinite(percent) ? percent : null;
    }, [passScore, contentType]);

    /**
     * A SCORM course is only playable once its package has been unpacked server-side, so
     * Publish is held back until the API reports `ready` — a learner must never be able to
     * open a course whose content does not exist yet. A failed package stays publishable-blocked
     * too; saving again re-runs the unpack.
     */
    const scormBlocksPublish = contentType === 'SCORM'
        && (!contentFile || course?.scormStatus !== 'ready');

    const scormNextStep = scormNextStepFor(contentType, Boolean(contentFile), course?.scormStatus ?? null);

    /**
     * While a package is being unpacked, re-read the course so the panel below can move from
     * processing to ready (or failed) without a manual refresh. Stops itself the moment the
     * status leaves the in-flight states.
     */
    useEffect(() => {
        if (courseId === null || contentType !== 'SCORM') return;
        if (course?.scormStatus !== 'pending' && course?.scormStatus !== 'processing') return;
        let cancelled = false;
        const timer = setInterval(async () => {
            try {
                const fresh = await learningApi.courses.get(courseId);
                if (!cancelled) setCourse(fresh);
            } catch {
                // Transient; the next tick tries again.
            }
        }, 3000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [courseId, contentType, course?.scormStatus]);

    const selectedCategory = useMemo(
        () => categoryOptions.find((category) => String(category.id) === categoryId) ?? null,
        [categoryOptions, categoryId],
    );

    const accent = selectedCategory?.colour && HEX_COLOUR.test(selectedCategory.colour)
        ? selectedCategory.colour
        : null;

    const durationMinutes = useMemo(() => {
        const minutes = Number(duration);
        return duration.trim() !== '' && Number.isFinite(minutes) ? minutes : null;
    }, [duration]);

    const certificateValidityMonths = useMemo(() => {
        const months = Number(certificateValidity);
        return certificateValidity.trim() !== '' && Number.isFinite(months) ? months : null;
    }, [certificateValidity]);

    /**
     * MultiSelect keys its command items by LABEL, so two departments sharing a name become
     * one item and a single click selects them all. Department names are not unique in the
     * data (unlike departmentCode), so disambiguate only the genuine duplicates with the code
     * — everyone else keeps the plain name.
     */
    const departmentOptions = useMemo(() => {
        const departments = options?.departments ?? [];
        const nameCounts = departments.reduce<Record<string, number>>((acc, department) => {
            acc[department.departmentName] = (acc[department.departmentName] ?? 0) + 1;
            return acc;
        }, {});
        return departments.map((department) => ({
            value: department.departmentCode,
            label: nameCounts[department.departmentName] > 1
                ? `${department.departmentName} (${department.departmentCode})`
                : department.departmentName,
        }));
    }, [options]);

    const acceptLabel = CONTENT_TYPES.find((type) => type.value === contentType)?.accept ?? '';
    /** The `accept` attribute for the content picker — mirrors what the backend allow-list permits. */
    const contentAccept = contentType === 'PPTX'
        ? '.pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation'
        : contentType === 'SCORM'
            ? '.zip,application/zip'
            : '.pdf,application/pdf';
    const statusLabel = course?.status ?? 'Draft';

    /**
     * Uploads happen before the course is saved: the file has to exist in S3 before the
     * course row can point at it, so the returned { url, key } travels on the save payload
     * rather than being a side effect of it.
     */
    const handleUpload = async (kind: 'thumbnail' | 'content', file: File) => {
        if (uploading) return;
        try {
            setUploading(kind);
            const result = kind === 'thumbnail'
                ? await learningApi.uploads.thumbnail(file, courseId ?? undefined)
                : await learningApi.uploads.content(file, courseId ?? undefined);
            if (kind === 'thumbnail') {
                setThumbnail({ url: result.url, key: result.key, previewUrl: result.previewUrl });
            } else {
                setContentFile({ url: result.url, key: result.key });
                // Replacing the package invalidates the current extraction, and the server only
                // finds out on the next save. Dropping the local `ready` here keeps Publish
                // shut until the new package has actually been unpacked — otherwise the gate
                // would still be reading the OLD package's status and let HR publish a course
                // whose content is mid-re-ingest.
                if (contentType === 'SCORM' && course) {
                    setCourse({ ...course, scormStatus: null });
                }
            }
            toast.success(kind === 'thumbnail' ? 'Thumbnail uploaded' : 'Course file uploaded');
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Upload failed');
        } finally {
            setUploading(null);
        }
    };

    const submit = async (status: 'Draft' | 'Active') => {
        if (saving) return;

        const trimmedTitle = title.trim();
        if (!trimmedTitle) {
            toast.error('Title is required');
            return;
        }
        if (status === 'Active' && !description.trim()) {
            toast.error('Description is required to publish');
            return;
        }
        if (!categoryId) {
            toast.error('Category is required');
            return;
        }
        if (status === 'Active' && scormBlocksPublish) {
            toast.error('The SCORM package has to finish processing first — save as a draft, then publish.');
            return;
        }

        const payload: CoursePayload = {
            title: trimmedTitle,
            description: description.trim() || null,
            categoryId: Number(categoryId),
            contentType,
            estimatedDurationMinutes: durationMinutes,
            passScorePercent,
            showInOnboarding,
            issuesCertificate,
            certificateValidityMonths: issuesCertificate ? certificateValidityMonths : null,
            thumbnailUrl: thumbnail?.url ?? null,
            thumbnailKey: thumbnail?.key ?? null,
            contentFileUrl: contentFile?.url ?? null,
            contentFileKey: contentFile?.key ?? null,
            status,
            visibility: {
                type: visibilityType,
                departmentCodes: visibilityType === 'Department' ? departmentCodes : undefined,
                jobRoles: visibilityType === 'JobRole' ? jobRoles : undefined,
            },
        };

        setSaving(status === 'Draft' ? 'draft' : 'publish');
        try {
            const saved = courseId !== null
                ? await learningApi.courses.update(courseId, payload)
                : await learningApi.courses.create(payload);

            toast.success(status === 'Draft' ? 'Course saved as draft' : 'Course published');

            // A SCORM package is only unpacked AFTER the save, so for that content type this
            // page is where the next step happens: staying here (and re-reading the course so
            // the poll below sees `pending`) is what lets HR watch it become publishable.
            // Bouncing to the library would leave them with a greyed-out Publish and no way
            // to fix it. When the save was a publish of an ALREADY-ready package there is
            // nothing to watch, so it exits like every other content type.
            if (contentType === 'SCORM' && saved && saved.id) {
                const fresh = await learningApi.courses.get(saved.id);
                const unpacking = fresh.scormStatus === 'pending' || fresh.scormStatus === 'processing';

                if (unpacking) {
                    if (courseId === null) {
                        // The note on the edit page explains the wait; the toast below would
                        // simply repeat it.
                        navigate(`/learning/courses/${fresh.id}/edit`, { replace: true });
                    } else {
                        setCourse(fresh);
                        toast.info('Unpacking the package — Publish unlocks when it finishes.');
                    }
                    return;
                }

                setCourse(fresh);
            }

            navigate('/learning/courses');
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to save course');
        } finally {
            setSaving(null);
        }
    };

    if (!canSave) return <AccessRestricted />;
    if (loading) return <LoadingPageSkeleton />;

    // `relative` on the wrapper is load-bearing: the `sr-only` controls below (the hidden
    // file inputs and the radio group items) are `position: absolute` with no explicit
    // offsets, so without a positioned ancestor they resolve against the initial containing
    // block — escaping main's scroll clip and stretching the document by ~590px. That made
    // the root scrollable, exposing the shell's dark background as a blank band at the
    // bottom of the page whenever the form grew past the viewport.
    return (
        <div className="relative space-y-4 p-2 md:space-y-6 md:p-0">
            {/* ─── Header ─── */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        size="icon"
                        type="button"
                        onClick={() => navigate('/learning/courses')}
                    >
                        <ArrowLeft size={16} />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold">{isEdit ? 'Edit Course' : 'New Course'}</h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Status <strong className="text-foreground">{statusLabel}</strong> · changes
                            to a published course never rewrite past assignments
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        type="button"
                        disabled={saving !== null}
                        onClick={() => submit('Draft')}
                    >
                        {saving === 'draft' && <Loader2 size={16} className="animate-spin" />}
                        Save as Draft
                    </Button>
                    <Button
                        type="button"
                        disabled={saving !== null || scormBlocksPublish}
                        title={scormBlocksPublish ? 'Waiting for the SCORM package to finish processing' : undefined}
                        onClick={() => submit('Active')}
                    >
                        {saving === 'publish'
                            ? <Loader2 size={16} className="animate-spin" />
                            : <Check size={16} />}
                        Publish
                    </Button>
                </div>
            </div>

            {scormNextStep && (
                <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs leading-relaxed text-amber-900">
                    <Info size={15} className="mt-0.5 flex-none" />
                    <span>{scormNextStep}</span>
                </div>
            )}

            <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
                {/* ─── Form ─── */}
                <div className="flex min-w-0 flex-col gap-4">
                    <Card>
                        <CardContent className="flex flex-col gap-4 p-4 md:p-6">
                            <SectionLabel>Course details</SectionLabel>

                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="course-title">Title</Label>
                                <Input
                                    id="course-title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="e.g. Working at Heights Refresher"
                                />
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="course-description">Description</Label>
                                <Textarea
                                    id="course-description"
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="One or two sentences — shown on the library card and the course landing page."
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                <div className="flex flex-col gap-1.5">
                                    <Label htmlFor="course-category">Category (required, single)</Label>
                                    <Select value={categoryId} onValueChange={setCategoryId}>
                                        <SelectTrigger id="course-category">
                                            <SelectValue placeholder="Select a category" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {categoryOptions.map((category) => (
                                                <SelectItem key={category.id} value={String(category.id)}>
                                                    {category.name}
                                                    {category.archived ? ' (archived)' : ''}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <Label htmlFor="course-duration">Estimated duration (min)</Label>
                                    <Input
                                        id="course-duration"
                                        type="number"
                                        min={0}
                                        value={duration}
                                        onChange={(e) => setDuration(e.target.value)}
                                        placeholder="20"
                                    />
                                </div>
                            </div>

                            {contentType === 'SCORM' && (
                                <div className="flex flex-col gap-1.5">
                                    <Label htmlFor="course-pass-score">Pass mark (%)</Label>
                                    <Input
                                        id="course-pass-score"
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={passScore}
                                        onChange={(e) => setPassScore(e.target.value)}
                                        placeholder="No pass mark"
                                    />
                                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                                        The package reports a score; anything below this leaves the course
                                        unfinished. Blank means the package's own result decides.
                                    </p>
                                </div>
                            )}

                            <div className="flex flex-col gap-2">
                                <Label>Thumbnail</Label>
                                <div className="flex items-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-3.5">
                                    <div
                                        className={cn(
                                            'flex h-11 w-16 flex-none items-center justify-center rounded-lg',
                                            !accent && 'bg-muted',
                                        )}
                                        style={accent ? { backgroundColor: `${accent}1f` } : undefined}
                                    >
                                        <ImageIcon
                                            size={18}
                                            className={cn(!accent && 'text-muted-foreground')}
                                            style={accent ? { color: accent } : undefined}
                                        />
                                    </div>
                                    <div className="text-xs leading-relaxed text-muted-foreground">
                                        {thumbnail ? 'Thumbnail attached.' : 'Choose an image for the card.'}
                                        <br />Optional: falls back to the category colour block.
                                        <input
                                            id="course-thumbnail"
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp,image/gif"
                                            className="sr-only"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) void handleUpload('thumbnail', file);
                                                e.target.value = '';
                                            }}
                                        />
                                        <label
                                            htmlFor="course-thumbnail"
                                            className="mt-1 block w-fit cursor-pointer text-blue-600 hover:underline"
                                        >
                                            {uploading === 'thumbnail' ? 'Uploading…' : 'Browse'}
                                        </label>
                                        {thumbnail && (
                                            <button
                                                type="button"
                                                className="mt-1 block text-[11px] text-destructive hover:underline"
                                                onClick={() => setThumbnail(null)}
                                            >
                                                Remove thumbnail
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="flex flex-col gap-4 p-4 md:p-6">
                            <SectionLabel>Content</SectionLabel>

                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="course-content-type">Content type</Label>
                                <Select
                                    value={contentType}
                                    onValueChange={(value) => setContentType(value as CourseContentType)}
                                >
                                    <SelectTrigger id="course-content-type">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CONTENT_TYPES.map((type) => (
                                            <SelectItem key={type.value} value={type.value}>
                                                {type.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-3.5">
                                <div className="text-xs text-muted-foreground">
                                    Accepted: <strong className="text-foreground">{acceptLabel}</strong>
                                    {contentFile && <span className="ml-1 text-emerald-600">· file attached</span>}
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        id="course-content-file"
                                        type="file"
                                        accept={contentAccept}
                                        className="sr-only"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) void handleUpload('content', file);
                                            e.target.value = '';
                                        }}
                                    />
                                    <Button variant="outline" size="sm" type="button" asChild>
                                        <label htmlFor="course-content-file" className="cursor-pointer">
                                            {uploading === 'content' ? 'Uploading…' : 'Choose file'}
                                        </label>
                                    </Button>
                                    {contentFile && (
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            type="button"
                                            className="text-destructive"
                                            aria-label="Remove attached file"
                                            title="Remove attached file"
                                            onClick={() => setContentFile(null)}
                                        >
                                            <Trash2 size={16} />
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {contentType === 'SCORM' && (
                                <ScormPackageNote course={course} hasFile={Boolean(contentFile)} />
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="flex flex-col gap-3.5 p-4 md:p-6">
                            <SectionLabel>Behaviour</SectionLabel>

                            <div className="flex items-start gap-2.5">
                                <Checkbox
                                    id="course-onboarding"
                                    className="mt-0.5"
                                    checked={showInOnboarding}
                                    onCheckedChange={(checked) => setShowInOnboarding(checked === true)}
                                />
                                <div className="text-sm">
                                    <Label htmlFor="course-onboarding" className="font-normal">
                                        Show in Onboarding
                                    </Label>
                                    <p className="mt-0.5 text-xs text-muted-foreground">
                                        Every new starter is auto-assigned this course (
                                        <code className="rounded bg-muted px-1 py-0.5 text-[11px]">
                                            enrollmentSource: Onboarding
                                        </code>
                                        ).
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-2.5">
                                <Checkbox
                                    id="course-certificate"
                                    className="mt-0.5"
                                    checked={issuesCertificate}
                                    onCheckedChange={(checked) => setIssuesCertificate(checked === true)}
                                />
                                <div className="text-sm">
                                    <Label htmlFor="course-certificate" className="font-normal">
                                        Issues Certificate
                                    </Label>
                                    <p className="mt-0.5 text-xs text-muted-foreground">
                                        A certificate number is allocated on completion; the PDF is
                                        generated on demand.
                                    </p>
                                </div>
                            </div>

                            {issuesCertificate && (
                                <div className="flex w-full max-w-[260px] flex-col gap-1.5">
                                    <Label htmlFor="course-certificate-validity">
                                        Certificate validity (months)
                                    </Label>
                                    <Input
                                        id="course-certificate-validity"
                                        type="number"
                                        min={0}
                                        value={certificateValidity}
                                        onChange={(e) => setCertificateValidity(e.target.value)}
                                        placeholder="12"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Leave blank for no expiry.
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="flex flex-col gap-3.5 p-4 md:p-6">
                            <SectionLabel>Course visibility (Browse Library)</SectionLabel>

                            <RadioGroup
                                value={visibilityType}
                                onValueChange={(value) => setVisibilityType(value as CourseVisibilityType)}
                            >
                                {VISIBILITY_OPTIONS.map((option) => {
                                    const selected = visibilityType === option.value;
                                    return (
                                        <label
                                            key={option.value}
                                            htmlFor={`visibility-${option.value}`}
                                            className={cn(
                                                // Card-shaped choice, reusing the selectable-card treatment
                                                // from TrainingPlanForm: the whole row is the hit target.
                                                // `focus-within` carries the keyboard affordance because the
                                                // radio glyph itself is visually hidden (see RadioGroupItem).
                                                'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors hover:bg-slate-50 focus-within:ring-2 focus-within:ring-ring',
                                                selected
                                                    ? 'border-primary bg-slate-50 ring-1 ring-primary/20'
                                                    : 'border-border',
                                            )}
                                        >
                                            <RadioGroupItem
                                                value={option.value}
                                                id={`visibility-${option.value}`}
                                                // The card's border + focus ring are the affordance, so the radio
                                                // glyph is redundant — visually hidden (still focusable and
                                                // announced) rather than restyling the shared primitive.
                                                className="sr-only"
                                            />
                                            <span className="flex flex-col">
                                                <span className="text-sm font-semibold">
                                                    {option.label}
                                                </span>
                                                <span className="mt-1 text-xs text-muted-foreground">
                                                    {option.description}
                                                </span>
                                            </span>
                                        </label>
                                    );
                                })}
                            </RadioGroup>

                            {visibilityType === 'Department' && (
                                <div className="flex flex-col gap-2">
                                    <div className="text-xs text-muted-foreground">
                                        Departments with access
                                    </div>
                                    <MultiSelect
                                        options={departmentOptions}
                                        selected={departmentCodes}
                                        onChange={setDepartmentCodes}
                                        placeholder="Select departments"
                                        emptyMessage="No departments found."
                                        maxDisplayTags={3}
                                    />
                                </div>
                            )}

                            {visibilityType === 'JobRole' && (
                                <div className="flex flex-col gap-2">
                                    <div className="text-xs text-muted-foreground">
                                        Job roles with access
                                    </div>
                                    <MultiSelect
                                        options={(options?.jobRoles ?? []).map((jobRole) => ({
                                            value: jobRole.title,
                                            label: jobRole.title,
                                        }))}
                                        selected={jobRoles}
                                        onChange={setJobRoles}
                                        placeholder="Select job roles"
                                        emptyMessage="No job roles found."
                                        maxDisplayTags={3}
                                    />
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* ─── Live Browse Library preview ─── */}
                <div className="flex flex-col gap-2.5 lg:sticky lg:top-4">
                    <div className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                        Landing-page preview · live
                    </div>
                    <LandingPagePreview
                        title={title}
                        description={description}
                        categoryName={selectedCategory?.name ?? null}
                        categoryColour={accent}
                        thumbnailUrl={thumbnail?.previewUrl ?? thumbnail?.url ?? null}
                        contentType={contentType}
                        durationMinutes={durationMinutes}
                        showInOnboarding={showInOnboarding}
                        issuesCertificate={issuesCertificate}
                    />
                </div>
            </div>
        </div>
    );
}
